import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import qs from "qs";
import debounce from "lodash/debounce";
import isBoolean from "lodash/isBoolean";
import isEqual from "lodash/isEqual";
import isArray from "lodash/isArray";
import axios from "../../axios";
import { usePrevious } from "../../utils/hooks/usePrevious";
import { saveTableParams } from "./methods";

/**
 * Custom hook that encapsulates data fetching, loading state and reload logic for SmartTable.
 * Extracted to reduce cyclomatic complexity of the SmartTable component.
 *
 * @param {Object}   options
 * @param {string}   options.route
 * @param {string}   options.name                      - kept for API compatibility (no longer used internally)
 * @param {Object}   options.tableService
 * @param {string}   options.tableLocalStorageKeyName
 * @param {Object}   options.tableState                - { shouldReload, resetFilters, deleted, deletedItems }
 * @param {Array}    options.staticData
 * @param {Function} options.transformer
 * @param {Function} options.transformerParams
 * @param {Function} options.onGetData
 * @param {Function} options.onError
 * @param {Function} options.onFetchStart              - called before each request (component-level side effects)
 * @param {Function} options.onFetchSuccess            - called after successful fetch (component-level side effects)
 */
const useTableFetch = ({
  route,
  name: _name, // eslint-disable-line no-unused-vars
  tableService,
  tableLocalStorageKeyName,
  tableState,
  staticData,
  transformer,
  transformerParams,
  onGetData,
  onError,
  onFetchStart,
  onFetchSuccess
}) => {
  const { shouldReload, resetFilters } = tableState;
  const previousShouldReload = usePrevious(shouldReload);

  const axiosControllerRef = useRef(null);

  const [data, setData] = useState(staticData || []);
  const [loading, setLoading] = useState(!staticData);
  const [initialized, setInitialized] = useState(!!staticData);
  const [search, setSearch] = useState(tableService.getSearchValue());

  const getTableData = useCallback(
    (params) => {
      if (axiosControllerRef.current) {
        axiosControllerRef.current.abort();
      }

      if (!route) return;

      axiosControllerRef.current = new AbortController();
      setLoading(true);
      onFetchStart?.();

      axios
        .get(route, {
          signal: axiosControllerRef.current.signal,
          params,
          paramsSerializer: (p) => qs.stringify(transformerParams(p), { arrayFormat: "brackets" })
        })
        .then(
          (res) => {
            const responseData = res.data.data?.data || (isArray(res.data.data) ? res.data.data : []);
            const preparedData = transformer(responseData);
            setData(preparedData);
            tableService
              .setPerPage(params.per_page)
              .setPage(res.data.data?.pager?.current || 1)
              .setTotal(res.data.data?.pager?.totalItems || preparedData.length);
            setLoading(false);
            setInitialized(true);
            onFetchSuccess?.();
            onGetData?.(preparedData, res.data.data?.pager);

            if (tableLocalStorageKeyName) {
              saveTableParams(tableService, tableLocalStorageKeyName);
            }
          },
          (err) => {
            setLoading(false);
            onError?.(err);
          }
        );
    },
    [
      route,
      tableService,
      tableLocalStorageKeyName,
      transformer,
      transformerParams,
      onGetData,
      onError,
      onFetchStart,
      onFetchSuccess
    ]
  );

  const getTableDataRef = useRef(getTableData);
  useEffect(() => {
    getTableDataRef.current = getTableData;
  }, [getTableData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceGetData = useMemo(() => debounce(() => getTableDataRef.current(tableService.getFilters()), 1000), []);

  // Initial load
  useEffect(() => {
    getTableData(tableService.getFilters());

    return () => {
      axiosControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload triggered from Redux tableState
  useEffect(() => {
    if (isBoolean(shouldReload) && !isEqual(shouldReload, previousShouldReload)) {
      const currentPage = tableService.getPage();
      const total = tableService.getTotal();
      const perPage = tableService.getPerPage();
      const lastPage = Math.ceil(total / perPage);

      if (
        (data.length === 1 || tableState.deletedItems === data.length) &&
        tableState.deleted &&
        lastPage === currentPage
      ) {
        tableService.setPage(currentPage - 1 || 1);
      }

      let params = tableService.getFilters();
      if (resetFilters) {
        params = tableService.removeAllFilters().getFilters();
        setSearch("");
      }

      getTableData(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  // Sync staticData changes
  const prevStaticData = usePrevious(staticData);
  useEffect(() => {
    if (!isEqual(staticData, prevStaticData)) {
      setData(staticData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticData]);

  return { data, loading, initialized, search, setSearch, getTableData, debounceGetData };
};

export default useTableFetch;
